import { useState, useRef, useEffect } from 'react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, Button, Input, Label, Select, Textarea } from '@/components/ui'
import { QrCode, Download, Copy } from 'lucide-react'
import { toast } from 'sonner'
import QRCode from 'qrcode'

export function QRGenerator() {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [text, setText] = useState('https://scbphil.com')
  const [size, setSize] = useState(256)
  const [fgColor, setFgColor] = useState('#000000')
  const [bgColor, setBgColor] = useState('#ffffff')
  const [errorLevel, setErrorLevel] = useState<'L' | 'M' | 'Q' | 'H'>('M')

  const generateQR = async () => {
    const canvas = canvasRef.current
    if (!canvas || !text.trim()) return

    try {
      await QRCode.toCanvas(canvas, text, {
        width: size,
        margin: 2,
        color: {
          dark: fgColor,
          light: bgColor,
        },
        errorCorrectionLevel: errorLevel,
      })
    } catch (err) {
      toast.error('Failed to generate QR code')
      console.error(err)
    }
  }

  useEffect(() => {
    generateQR()
  }, [text, size, fgColor, bgColor, errorLevel])

  const downloadQR = async (format: 'png' | 'svg') => {
    if (!text.trim()) {
      toast.error('Please enter content first')
      return
    }

    try {
      if (format === 'png') {
        const dataUrl = await QRCode.toDataURL(text, {
          width: size,
          margin: 2,
          color: {
            dark: fgColor,
            light: bgColor,
          },
          errorCorrectionLevel: errorLevel,
        })
        const link = document.createElement('a')
        link.download = 'qrcode.png'
        link.href = dataUrl
        link.click()
        toast.success('QR code downloaded as PNG')
      } else {
        const svgString = await QRCode.toString(text, {
          type: 'svg',
          width: size,
          margin: 2,
          color: {
            dark: fgColor,
            light: bgColor,
          },
          errorCorrectionLevel: errorLevel,
        })
        const blob = new Blob([svgString], { type: 'image/svg+xml' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.download = 'qrcode.svg'
        link.href = url
        link.click()
        URL.revokeObjectURL(url)
        toast.success('QR code downloaded as SVG')
      }
    } catch (err) {
      toast.error('Failed to download QR code')
      console.error(err)
    }
  }

  const copyToClipboard = async () => {
    const canvas = canvasRef.current
    if (!canvas) return

    try {
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((b) => {
          if (b) resolve(b)
          else reject(new Error('Failed to create blob'))
        }, 'image/png')
      })
      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': blob })
      ])
      toast.success('QR code copied to clipboard')
    } catch {
      toast.error('Failed to copy to clipboard')
    }
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
            <QrCode className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">QR Code Generator</h1>
            <p className="text-muted-foreground">Generate scannable QR codes for URLs, text, and more</p>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_400px]">
        {/* Settings */}
        <Card className="glass">
          <CardHeader>
            <CardTitle>Settings</CardTitle>
            <CardDescription>Configure your QR code</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="content">Content</Label>
              <Textarea
                id="content"
                placeholder="Enter URL or text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                className="min-h-[100px]"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="size">Size (px)</Label>
                <Select
                  id="size"
                  value={String(size)}
                  onChange={(e) => setSize(Number(e.target.value))}
                >
                  <option value="128">128 x 128</option>
                  <option value="256">256 x 256</option>
                  <option value="512">512 x 512</option>
                  <option value="1024">1024 x 1024</option>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="error">Error Correction</Label>
                <Select
                  id="error"
                  value={errorLevel}
                  onChange={(e) => setErrorLevel(e.target.value as 'L' | 'M' | 'Q' | 'H')}
                >
                  <option value="L">Low (7%)</option>
                  <option value="M">Medium (15%)</option>
                  <option value="Q">Quartile (25%)</option>
                  <option value="H">High (30%)</option>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="fg-color">Foreground Color</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    id="fg-color"
                    value={fgColor}
                    onChange={(e) => setFgColor(e.target.value)}
                    className="w-12 h-10 p-1 cursor-pointer"
                  />
                  <Input
                    value={fgColor}
                    onChange={(e) => setFgColor(e.target.value)}
                    className="flex-1 font-mono"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="bg-color">Background Color</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    id="bg-color"
                    value={bgColor}
                    onChange={(e) => setBgColor(e.target.value)}
                    className="w-12 h-10 p-1 cursor-pointer"
                  />
                  <Input
                    value={bgColor}
                    onChange={(e) => setBgColor(e.target.value)}
                    className="flex-1 font-mono"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Preview */}
        <Card className="glass">
          <CardHeader>
            <CardTitle>Preview</CardTitle>
            <CardDescription>Your generated QR code</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-center p-4 bg-muted/20 rounded-lg">
              <canvas
                ref={canvasRef}
                className="rounded-lg"
                style={{ maxWidth: '100%', height: 'auto' }}
              />
            </div>

            <div className="flex gap-2">
              <Button onClick={() => downloadQR('png')} className="flex-1">
                <Download className="h-4 w-4 mr-2" />
                PNG
              </Button>
              <Button onClick={() => downloadQR('svg')} variant="outline" className="flex-1">
                <Download className="h-4 w-4 mr-2" />
                SVG
              </Button>
              <Button onClick={copyToClipboard} variant="outline" size="icon">
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
